import { defineStore } from "pinia";
import { ref, getCurrentInstance } from "vue";
import useMyFetch from "@stores/useMyFetch";
import { camelizeObject } from "@utils/toolBox";
import { getClientId } from "@utils/clientId";
import { useToastsStore } from "@/stores/toastsStore";
import { TASK_HANDLERS } from "@/stores/taskHandlers";

const TERMINAL_STATUSES = ["Complete", "Failed"];

function buildMessage(task) {
  let base = task.message;
  if (!base) {
    if (task.status === "Complete") base = "Done";
    else if (task.status === "Failed") base = "Failed";
    else base = task.status;
  }
  if (typeof task.details === "string" && task.details) {
    return `${base} · ${task.details}`;
  }
  return base;
}

export const useTaskProgressesStore = defineStore("taskProgresses", () => {
  const toastApi = useToastsStore();
  const taskProgresses = ref([]);
  const lastProgressUpdate = ref({});

  function upsertTask(task) {
    const index = taskProgresses.value.findIndex((t) => t.id === task.id);
    if (index === -1) {
      taskProgresses.value = [task, ...taskProgresses.value];
    } else {
      taskProgresses.value[index] = task;
    }
  }

  function removeTask(taskId) {
    taskProgresses.value = taskProgresses.value.filter((t) => t.id !== taskId);
  }

  function showOrUpdateToast(task) {
    const id = `task-${task.id}`;
    const title = task.taskName;
    const message = buildMessage(task);
    const toastComponent = TASK_HANDLERS[task.taskInfo?.type]?.toastComponent;
    const componentOpts = toastComponent ? { component: toastComponent, componentProps: { task } } : {};

    if (TERMINAL_STATUSES.includes(task.status)) {
      const cleanup = () => removeTask(task.id);
      const opts = { id, onDismiss: cleanup, onAutoClose: cleanup, ...componentOpts };
      toastApi[task.status === "Complete" ? "success" : "error"](title, message, opts);
    } else {
      toastApi.loading(title, message, { id, progress: task.percentage, ...componentOpts });
    }
  }

  const handleProgressUpdate = (msg) => {
    const data = camelizeObject(JSON.parse(msg));
    lastProgressUpdate.value = data;
    upsertTask(data);
    showOrUpdateToast(data);
    // Side effects (e.g. refreshing a list) run only on live completion, not when
    // replaying historical tasks on page load.
    if (data.status === "Complete") TASK_HANDLERS[data.taskInfo?.type]?.onComplete?.(data);
    if (data.status === "Failed") TASK_HANDLERS[data.taskInfo?.type]?.onFailed?.(data);
  };

  const fetchTaskProgresses = async () => {
    try {
      const clientId = getClientId();
      const { data } = await useMyFetch(`/task_progresses?client_id=${clientId}`).json();
      data.value.forEach((raw) => {
        const task = camelizeObject(JSON.parse(raw));
        upsertTask(task);
        showOrUpdateToast(task);
      });
    } catch (err) {
      console.error(err);
    }
  };

  const initializeTaskProgresses = () => {
    const instance = getCurrentInstance();
    const cable = instance.proxy.$cable;
    cable.subscribe({ channel: "TaskProgressChannel" });
    cable._addChannel(
      "TaskProgressChannel",
      { received: handleProgressUpdate },
      { _uid: instance.uid, _value: instance.proxy }
    );
    fetchTaskProgresses();
  };

  return {
    taskProgresses,
    lastProgressUpdate,
    initializeTaskProgresses,
  };
});
